/**
* NAATI comparison cluster — /naati/compare/{slug}.
*
* Decision-stage pages: each entry compares NAATI certified translation with
* an alternative route (consular legalization, notary public, sworn
* translator, apostille, self-translation, etc.) using an answer-first
* verdict, a factual comparison table and 3 FAQ entries.
*
* Accuracy policy: prices/turnaround are ranges marked "โดยประมาณ"; no
* approval guarantees — acceptance is always at the receiving authority's
* discretion.
*/

export interface NaatiCompareRow {
  aspect: string;
  a: string;
  b: string;
}

export interface NaatiCompare {
  slug: string;
  title: string;
  description: string;
  h1: string;
  aLabel: string;
  bLabel: string;
  answer: string;
  rows: NaatiCompareRow[];
  chooseA: string[];
  chooseB: string[];
  faqs: Array<{ q: string; a: string }>;
  synonyms: string[];
}

const TAIL =
  "เงื่อนไขของหน่วยงานปลายทางเปลี่ยนแปลงได้ โปรดตรวจคำสั่งล่าสุดกับผู้รับเอกสารก่อนยื่น ผลการพิจารณาเป็นดุลพินิจของหน่วยงานนั้น";

export const NAATI_COMPARE: NaatiCompare[] = [
  {
    slug: "naati-vs-consular-legalization",
    title: "แปล NAATI กับ รับรองกรมการกงสุล (MFA) ต่างกันอย่างไร เลือกอันไหน",
    description:
      "เทียบชัดทีละข้อ: แปล NAATI กับรับรองกรมการกงสุล ใช้กับหน่วยงานไหน ใช้เวลาเท่าไร ต้องทำทั้งคู่หรือไม่ พร้อมเช็กลิสต์ก่อนยื่น",
    h1: "แปล NAATI vs รับรองกรมการกงสุล (MFA) — ต่างกันอย่างไร",
    aLabel: "แปล NAATI Certified",
    bLabel: "รับรองกรมการกงสุล (MFA)",
    answer:
      "ทั้งสองอย่างรับรองคนละเรื่อง NAATI รับรองว่า “คำแปลถูกต้อง” โดยนักแปลที่ออสเตรเลียรับรอง ส่วนกรมการกงสุลรับรองว่า “เอกสาร/ลายมือชื่อเจ้าหน้าที่เป็นของจริง” เพื่อนำไปใช้ต่างประเทศ ถ้าปลายทางคือหน่วยงานออสเตรเลียหรือนิวซีแลนด์ ปกติใช้ NAATI อย่างเดียวจบ ถ้าปลายทางเป็นประเทศอื่นหรือใช้ทำนิติกรรมในไทย มักต้องผ่านกงสุลและสถานทูตปลายทางแทน",
    rows: [
      { aspect: "รับรองอะไร", a: "ความถูกต้องของคำแปล", b: "ความถูกต้องของเอกสาร/ลายมือชื่อราชการ" },
      { aspect: "ผู้รับรอง", a: "นักแปลที่มี NAATI Certification + Practitioner ID", b: "กรมการกงสุล กระทรวงการต่างประเทศ" },
      { aspect: "ปลายทางที่ยอมรับ", a: "DHA, INZ, AHPRA, VETASSESS, มหาวิทยาลัยออสเตรเลีย/นิวซีแลนด์", b: "สถานทูตต่างประเทศในไทย หน่วยงานราชการไทย และประเทศปลายทางที่ต้องรับรองซ้อน" },
      { aspect: "ระยะเวลา", a: "1-3 วันทำการ (โดยประมาณ)", b: "2-5 วันทำการขึ้นไป ยังไม่รวมคิวสถานทูต (โดยประมาณ)" },
      { aspect: "ตรวจสอบย้อนกลับ", a: "ตรวจหมายเลข Practitioner ได้บน naati.com.au", b: "ตรวจจากตราครุฑและเลขทะเบียนรับรอง" },
      { aspect: "ต้องมีต้นฉบับตัวจริงไหม", a: "ส่วนใหญ่ใช้สแกนคุณภาพสูงได้", b: "ต้องใช้ต้นฉบับหรือสำเนาที่หน่วยงานออกให้" },
    ],
    chooseA: [
      "ยื่นวีซ่า/PR/สัญชาติออสเตรเลีย หรือวีซ่านิวซีแลนด์",
      "ยื่น Skill Assessment, AHPRA, มหาวิทยาลัยในออสเตรเลีย",
      "ต้องการไฟล์ PDF พร้อมยื่นออนไลน์ภายใน 1-3 วัน",
    ],
    chooseB: [
      "ใช้เอกสารไทยกับประเทศที่ไม่ใช่ออสเตรเลีย/นิวซีแลนด์",
      "หน่วยงานปลายทางระบุให้ผ่านกงสุลและสถานทูต",
      "ใช้ทำนิติกรรมหรือยื่นต่อหน่วยงานราชการไทย",
    ],
    faqs: [
      { q: "ต้องทำทั้ง NAATI และกงสุลพร้อมกันไหม?", a: "ส่วนใหญ่ไม่ต้อง ให้ดูข้อกำหนดของผู้รับเอกสารเป็นหลัก บางกรณี เช่น ต้องใช้ทั้งในไทยและออสเตรเลีย จึงทำควบคู่กัน " + TAIL },
      { q: "กงสุลรับรองคำแปลเป็นภาษาอังกฤษได้ไหม?", a: "กรมการกงสุลรับรองคำแปลไทย-อังกฤษได้ แต่คำแปลนั้นไม่ได้แทนคำแปล NAATI สำหรับหน่วยงานออสเตรเลียที่ระบุ NAATI ไว้ชัดเจน" },
      { q: "อย่างไหนถูกกว่า?", a: "ค่าใช้จ่ายขึ้นกับจำนวนหน้าและจำนวนขั้นตอน เส้นทางกงสุล+สถานทูตมักมีหลายค่าธรรมเนียมและใช้เวลานานกว่า ขอใบเสนอราคาตามชุดเอกสารจริงจะแม่นที่สุด" },
    ],
    synonyms: ["naati vs mfa", "naati หรือ กงสุล", "naati vs consular legalization"],
  },
  {
    slug: "naati-vs-notary-public",
    title: "แปล NAATI กับ Notary Public ต่างกันอย่างไร ต้องใช้ทั้งคู่ไหม",
    description:
      "Notary Public รับรองลายมือชื่อ/สำเนา ส่วน NAATI รับรองคำแปล เทียบให้เห็นภาพว่าเคสไหนใช้อะไร และเมื่อไรต้องใช้ทั้งสองอย่าง",
    h1: "แปล NAATI vs Notary Public (โนตารี) — ใช้ต่างกันตรงไหน",
    aLabel: "แปล NAATI Certified",
    bLabel: "Notarial Services Attorney (Notary Public)",
    answer:
      "NAATI รับรองว่าคำแปลตรงกับต้นฉบับ ส่วน Notary Public (ในไทยคือทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร) รับรองลายมือชื่อ ตัวบุคคล หรือความถูกต้องของสำเนา ไม่ได้รับรองคุณภาพการแปล การยื่นวีซ่าออสเตรเลียโดยทั่วไปใช้คำแปล NAATI ก็เพียงพอ ส่วนเอกสารประเภทหนังสือมอบอำนาจหรือ Affidavit ที่ต้องมีการลงนามต่อหน้า จึงจะต้องใช้ Notary",
    rows: [
      { aspect: "รับรองอะไร", a: "ความถูกต้องของคำแปล", b: "ลายมือชื่อ ตัวบุคคล หรือสำเนาถูกต้อง" },
      { aspect: "ใช้กับเอกสารแบบไหน", a: "สูติบัตร ทะเบียนบ้าน ทรานสคริปต์ ใบรับรองต่าง ๆ", b: "หนังสือมอบอำนาจ Affidavit Statutory Declaration สำเนาพาสปอร์ต" },
      { aspect: "ต้องมาลงนามต่อหน้าไหม", a: "ไม่ต้อง ส่งไฟล์สแกนได้", b: "ต้องลงนามต่อหน้าโนตารี พร้อมบัตรประชาชน/พาสปอร์ต" },
      { aspect: "ระยะเวลา", a: "1-3 วันทำการ (โดยประมาณ)", b: "ทำได้ภายในวันเดียวหากนัดหมายล่วงหน้า (โดยประมาณ)" },
      { aspect: "ใช้ร่วมกันได้ไหม", a: "ได้ — แปลก่อนแล้วรับรองลายมือชื่อ", b: "ได้ — โนตารีรับรองแล้วจึงแปล ขึ้นกับข้อกำหนดปลายทาง" },
    ],
    chooseA: ["ยื่นเอกสารราชการที่ไม่ต้องเซ็นใหม่", "หน่วยงานออสเตรเลีย/นิวซีแลนด์ระบุ NAATI"],
    chooseB: ["ต้องเซ็นหนังสือมอบอำนาจส่งไปต่างประเทศ", "ปลายทางขอ certified true copy หรือ Affidavit"],
    faqs: [
      { q: "ยื่นวีซ่าออสเตรเลียต้องผ่าน Notary ด้วยไหม?", a: "โดยทั่วไปไม่ต้อง หากเอกสารเป็นเอกสารราชการและมีคำแปล NAATI ครบชุด " + TAIL },
      { q: "โนตารีในไทยเหมือน Notary Public ต่างประเทศไหม?", a: "ในไทยใช้ระบบทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารตามข้อบังคับสภาทนายความ ซึ่งทำหน้าที่คล้ายกันแต่กรอบอำนาจกำหนดตามข้อบังคับไทย" },
      { q: "ทำสองอย่างในที่เดียวได้ไหม?", a: "ได้ สำนักงานเราให้บริการทั้งงานแปล NAATI และงานรับรองลายมือชื่อในที่เดียว ลดการวิ่งเอกสารหลายรอบ" },
    ],
    synonyms: ["naati notary difference", "โนตารี กับ naati", "naati vs notary public"],
  },
  {
    slug: "naati-vs-apostille",
    title: "แปล NAATI กับ Apostille ต่างกันอย่างไร ไทยเข้าร่วมแล้วต้องทำอะไรบ้าง",
    description:
      "Apostille รับรองที่มาของเอกสาร ส่วน NAATI รับรองคำแปล เทียบให้ครบพร้อมสถานะการเข้าร่วมอนุสัญญาของไทยและผลต่อการยื่นเอกสารออสเตรเลีย",
    h1: "แปล NAATI vs Apostille — คนละหน้าที่ ใช้ผิดเสียเวลา",
    aLabel: "แปล NAATI Certified",
    bLabel: "Apostille (อนุสัญญากรุงเฮก)",
    answer:
      "Apostille คือใบรับรองที่ยืนยันที่มาของเอกสารราชการเพื่อใช้ในประเทศภาคีอนุสัญญา ไม่ได้รับรองคำแปล ส่วน NAATI รับรองคำแปลอย่างเดียว ออสเตรเลียเป็นภาคีอนุสัญญาแล้ว และไทยได้ดำเนินการเข้าร่วม โดยจะมีผลใช้บังคับในวันที่ 28 กุมภาพันธ์ 2027 ก่อนถึงวันดังกล่าวเอกสารไทยยังใช้เส้นทางรับรองกงสุลตามเดิม",
    rows: [
      { aspect: "รับรองอะไร", a: "คำแปลตรงกับต้นฉบับ", b: "ที่มาของเอกสาร ลายมือชื่อและตราของผู้ออกเอกสาร" },
      { aspect: "ผู้ออก", a: "นักแปล NAATI Certified", b: "หน่วยงานผู้มีอำนาจออก Apostille ของประเทศต้นทาง" },
      { aspect: "แทนกันได้ไหม", a: "ไม่ได้", b: "ไม่ได้ — หลายกรณีต้องมีทั้งคู่" },
      { aspect: "สถานะสำหรับเอกสารไทย", a: "ใช้ได้ทันทีวันนี้", b: "มีผลใช้บังคับ 28 ก.พ. 2027" },
      { aspect: "ยื่นวีซ่าออสเตรเลีย", a: "จำเป็นเมื่อเอกสารไม่ใช่ภาษาอังกฤษ", b: "โดยทั่วไปไม่ได้ถูกร้องขอสำหรับการยื่นวีซ่า" },
    ],
    chooseA: ["เอกสารภาษาไทยที่ต้องยื่นหน่วยงานออสเตรเลีย/นิวซีแลนด์"],
    chooseB: ["ปลายทางในประเทศภาคีอนุสัญญาระบุขอ Apostille โดยเฉพาะ เช่น งานทะเบียนสมรส งานมรดก"],
    faqs: [
      { q: "มี Apostille แล้วไม่ต้องแปล NAATI ใช่ไหม?", a: "ไม่ใช่ Apostille ไม่ได้แปลเอกสาร หากหน่วยงานปลายทางต้องการเอกสารภาษาอังกฤษ ยังต้องมีคำแปลที่รับรองอยู่ดี " + TAIL },
      { q: "ต้องแปลก่อนหรือขอ Apostille ก่อน?", a: "ลำดับขึ้นกับข้อกำหนดปลายทาง โดยทั่วไปรับรองเอกสารต้นฉบับให้ครบก่อน แล้วจึงแปลทั้งชุดรวมตราประทับ" },
      { q: "หลัง 28 ก.พ. 2027 กระบวนการจะสั้นลงไหม?", a: "สำหรับประเทศภาคี การรับรองซ้อนที่สถานทูตมีแนวโน้มลดลง แต่ควรตรวจแนวปฏิบัติที่หน่วยงานประกาศจริงในช่วงเปลี่ยนผ่าน" },
    ],
    synonyms: ["naati vs apostille", "apostille ต้องแปลไหม", "อาโพสทีล naati"],
  },
  {
    slug: "naati-vs-sworn-translator",
    title: "NAATI กับนักแปลสาบานตน (Sworn Translator) ต่างกันอย่างไร",
    description:
      "ยุโรปใช้ sworn translator ออสเตรเลียใช้ NAATI เทียบระบบรับรองนักแปลสองแบบ พร้อมแนวทางเลือกให้ตรงกับประเทศปลายทาง",
    h1: "NAATI vs Sworn Translator — ระบบรับรองนักแปลคนละมาตรฐาน",
    aLabel: "NAATI Certified Translator",
    bLabel: "Sworn / Court-appointed Translator",
    answer:
      "NAATI เป็นระบบรับรองของออสเตรเลีย ส่วน sworn translator เป็นระบบของหลายประเทศในยุโรปที่นักแปลสาบานตนต่อศาลหรือหน่วยงานรัฐ ทั้งสองระบบไม่รับรองข้ามกันโดยอัตโนมัติ ต้องเลือกให้ตรงกับประเทศที่จะยื่นเอกสาร",
    rows: [
      { aspect: "ประเทศที่ใช้", a: "ออสเตรเลีย นิวซีแลนด์ และหน่วยงานที่อ้างอิงมาตรฐานนี้", b: "เยอรมนี ฝรั่งเศส สเปน อิตาลี โปแลนด์ ฯลฯ" },
      { aspect: "ที่มาของอำนาจ", a: "ผ่านการรับรองโดย NAATI และต่ออายุทุก 3 ปี", b: "สาบานตนต่อศาล/หน่วยงานของประเทศนั้น" },
      { aspect: "ตรวจสอบอย่างไร", a: "ค้น Practitioner ID บน naati.com.au", b: "ตรวจทะเบียนนักแปลของศาลหรือกระทรวงยุติธรรมประเทศนั้น" },
      { aspect: "ใช้แทนกันได้ไหม", a: "ไม่ควรใช้กับปลายทางยุโรปที่ระบุ sworn", b: "ไม่ควรใช้กับหน่วยงานออสเตรเลียที่ระบุ NAATI" },
    ],
    chooseA: ["ปลายทางออสเตรเลีย/นิวซีแลนด์"],
    chooseB: ["ปลายทางประเทศยุโรปที่ระบุนักแปลสาบานตน"],
    faqs: [
      { q: "ยื่นทั้งออสเตรเลียและยุโรปพร้อมกันทำอย่างไร?", a: "แยกทำสองชุดตามมาตรฐานของแต่ละปลายทาง ใช้ต้นฉบับชุดเดียวกันได้ แต่คำแปลต้องออกโดยผู้มีคุณสมบัติของระบบนั้น ๆ " + TAIL },
      { q: "ราคาต่างกันมากไหม?", a: "ต่างกันตามภาษาและประเทศ ควรเทียบตามชุดเอกสารจริง เพราะจำนวนหน้าและค่าธรรมเนียมรับรองต่างกัน" },
      { q: "ในไทยมีนักแปลสาบานตนไหม?", a: "ไทยไม่มีระบบ sworn translator แบบยุโรป จึงใช้การรับรองคำแปลโดยหน่วยงานหรือทนายความผู้ทำคำรับรองแทน" },
    ],
    synonyms: ["sworn translator vs naati", "นักแปลสาบานตน naati"],
  },
  {
    slug: "naati-vs-self-translation",
    title: "แปลเองยื่นวีซ่าออสเตรเลียได้ไหม เทียบกับใช้นักแปล NAATI",
    description:
      "ทำไมการแปลเองหรือใช้ร้านแปลทั่วไปจึงเสี่ยงถูกขอเอกสารเพิ่ม เทียบข้อกำหนดจริงกับคำแปลที่รับรองโดยนักแปล NAATI",
    h1: "แปลเอง vs ใช้นักแปล NAATI — ความเสี่ยงที่มองไม่เห็น",
    aLabel: "แปลเอง / ร้านแปลทั่วไป",
    bLabel: "นักแปล NAATI Certified",
    answer:
      "หน่วยงานออสเตรเลียกำหนดให้คำแปลที่ทำในต่างประเทศต้องมาจากนักแปลที่มีคุณสมบัติและระบุชื่อ ที่อยู่ คุณวุฒิของผู้แปลไว้บนเอกสาร การแปลเองหรือให้ญาติแปลจึงมักถูกตีกลับหรือถูกขอเอกสารเพิ่ม ทำให้เสียเวลาหลายสัปดาห์ถึงหลายเดือน",
    rows: [
      { aspect: "ระบุคุณวุฒิผู้แปล", a: "ไม่มี หรือไม่ครบ", b: "มีชื่อ หมายเลข Practitioner และวันหมดอายุ" },
      { aspect: "ตรวจสอบย้อนกลับ", a: "ทำไม่ได้", b: "ตรวจได้จาก directory ทางการ" },
      { aspect: "โอกาสถูกขอเอกสารเพิ่ม", a: "สูง", b: "ต่ำกว่าอย่างมีนัยสำคัญเมื่อเอกสารครบถ้วน" },
      { aspect: "ต้นทุนรวม", a: "ถูกตอนแรก แต่ต้องทำใหม่หากถูกตีกลับ", b: "จ่ายครั้งเดียวพร้อมงานตรวจทาน" },
      { aspect: "รับผิดชอบแก้ไข", a: "ไม่มีผู้รับผิดชอบ", b: "แก้ไข-ออกฉบับใหม่ให้เมื่อเกิดจากงานแปลของเรา" },
    ],
    chooseA: ["ใช้ภายในครอบครัวหรือเพื่ออ่านทำความเข้าใจเท่านั้น"],
    chooseB: ["ทุกกรณีที่ต้องยื่นหน่วยงานราชการ สถาบันการศึกษา หรือหน่วยงานวิชาชีพ"],
    faqs: [
      { q: "ญาติที่เก่งอังกฤษแปลให้ได้ไหม?", a: "ไม่แนะนำสำหรับเอกสารยื่นทางการ เพราะขาดคุณสมบัติผู้แปลที่ตรวจสอบได้ และมีประเด็นเรื่องผลประโยชน์ทับซ้อน " + TAIL },
      { q: "ถ้าถูกตีกลับแล้วต้องทำอย่างไร?", a: "ส่งเอกสารเดิมพร้อมหนังสือแจ้งของหน่วยงานมาให้เราตรวจ เราจะระบุจุดที่ขาดและแปลใหม่ให้ครบตามที่ร้องขอ" },
      { q: "ใช้เครื่องมือแปลอัตโนมัติได้ไหม?", a: "ใช้เป็นตัวช่วยร่างได้ แต่เอกสารที่ยื่นต้องผ่านนักแปลที่รับรองและตรวจทานโดยมนุษย์เสมอ" },
    ],
    synonyms: ["แปลเองยื่นวีซ่าได้ไหม", "self translation australian visa", "ร้านแปลทั่วไป naati"],
  },
  {
    slug: "naati-digital-vs-wet-stamp",
    title: "คำแปล NAATI แบบไฟล์ดิจิทัล กับ ตัวจริงตราประทับหมึก ใช้ต่างกันอย่างไร",
    description:
      "ยื่นออนไลน์ใช้ PDF ได้ไหม เมื่อไรต้องใช้ตัวจริง เทียบข้อดี-ข้อจำกัดของคำแปล NAATI แบบดิจิทัลและแบบตราหมึก",
    h1: "NAATI ไฟล์ดิจิทัล vs ตัวจริงตราประทับหมึก",
    aLabel: "ไฟล์ PDF ลงนามดิจิทัล",
    bLabel: "ฉบับพิมพ์ + ตราประทับหมึก",
    answer:
      "การยื่นออนไลน์ผ่าน ImmiAccount หรือระบบของมหาวิทยาลัยส่วนใหญ่รับไฟล์ PDF ที่มีตราและลายมือชื่อนักแปลครบถ้วน ส่วนงานที่ต้องยื่นเคาน์เตอร์ หน่วยงานทะเบียน หรือหน่วยงานที่ระบุ original จึงต้องใช้ฉบับพิมพ์ที่มีตราหมึก แนะนำสั่งทั้งสองแบบเมื่อไม่แน่ใจ",
    rows: [
      { aspect: "เวลาได้รับ", a: "ภายใน 1-3 วันทำการ (โดยประมาณ)", b: "บวกเวลาจัดส่ง 1-4 วันในไทย" },
      { aspect: "ยื่นออนไลน์", a: "ใช้ได้เป็นส่วนใหญ่", b: "ต้องสแกนก่อนอัปโหลด" },
      { aspect: "ยื่นเคาน์เตอร์/ทะเบียน", a: "บางแห่งไม่รับ", b: "ใช้ได้" },
      { aspect: "ค่าจัดส่ง", a: "ไม่มี", b: "มีค่าไปรษณีย์หรือ courier ตามปลายทาง" },
      { aspect: "สำเนาเพิ่ม", a: "ทำซ้ำได้ทันที", b: "ต้องออกฉบับเพิ่มและจัดส่งใหม่" },
    ],
    chooseA: ["ยื่นวีซ่าออนไลน์ สมัครเรียน ส่งอีเมลให้ case officer"],
    chooseB: ["หน่วยงานระบุ certified hard copy หรือให้ยื่นด้วยตนเอง"],
    faqs: [
      { q: "PDF ถือว่าเป็นต้นฉบับไหม?", a: "ถือเป็นคำแปลที่รับรองแล้วหากตรา ลายมือชื่อ และข้อความรับรองครบถ้วน แต่หน่วยงานบางแห่งยังกำหนดให้ส่งฉบับพิมพ์ " + TAIL },
      { q: "ขอทั้งสองแบบต้องจ่ายสองรอบไหม?", a: "ไม่ ค่าแปลคิดครั้งเดียว เพิ่มเฉพาะค่าพิมพ์และค่าจัดส่งตามจริง" },
      { q: "ส่งต่างประเทศได้ไหม?", a: "ได้ ส่ง courier ระหว่างประเทศได้ตามปลายทางที่ลูกค้าระบุ พร้อมเลขติดตามพัสดุ" },
    ],
    synonyms: ["naati pdf ใช้ได้ไหม", "naati digital stamp", "naati hard copy"],
  },
  {
    slug: "naati-vs-inz-translation",
    title: "แปลยื่นนิวซีแลนด์ (INZ) ต้องใช้ NAATI ไหม เทียบข้อกำหนดกับออสเตรเลีย",
    description:
      "Immigration New Zealand ยอมรับคำแปลจากผู้แปลที่มีคุณสมบัติและเป็นกลาง เทียบข้อกำหนดกับ DHA ของออสเตรเลียให้เห็นภาพก่อนสั่งแปล",
    h1: "ยื่นนิวซีแลนด์ (INZ) vs ออสเตรเลีย (DHA) — ข้อกำหนดคำแปลต่างกันตรงไหน",
    aLabel: "ออสเตรเลีย (DHA)",
    bLabel: "นิวซีแลนด์ (INZ)",
    answer:
      "DHA กำหนดให้คำแปลที่ทำนอกออสเตรเลียต้องระบุชื่อ ที่อยู่ คุณวุฒิและประสบการณ์ของผู้แปล ส่วน INZ กำหนดให้คำแปลมาจากผู้แปลที่มีคุณสมบัติ เป็นกลาง และไม่ใช่ตัวผู้สมัครหรือญาติ คำแปลจากนักแปล NAATI Certified ตอบโจทย์ทั้งสองเงื่อนไข จึงใช้ชุดเดียวยื่นได้ทั้งสองประเทศในหลายกรณี",
    rows: [
      { aspect: "ระบุ NAATI ตรงตัวไหม", a: "อ้างอิงคุณสมบัติผู้แปล และ NAATI เป็นมาตรฐานที่ยอมรับกว้างที่สุด", b: "ไม่ได้บังคับ NAATI แต่ต้องเป็นผู้แปลที่มีคุณสมบัติและเป็นกลาง" },
      { aspect: "ห้ามผู้สมัครแปลเอง", a: "ใช่", b: "ใช่" },
      { aspect: "ต้องมีคำรับรองความถูกต้อง", a: "ใช่", b: "ใช่" },
      { aspect: "รับไฟล์ดิจิทัล", a: "รับสำหรับการยื่นออนไลน์", b: "รับสำหรับการยื่นออนไลน์" },
    ],
    chooseA: ["ยื่นวีซ่า/PR ออสเตรเลีย หรือหน่วยงานวิชาชีพออสเตรเลีย"],
    chooseB: ["ยื่นวีซ่านิวซีแลนด์ NZQA หรือหน่วยงานนิวซีแลนด์"],
    faqs: [
      { q: "ชุดเดียวยื่นได้ทั้งสองประเทศไหม?", a: "ในหลายกรณีได้ เพราะคำแปล NAATI ระบุคุณวุฒิผู้แปลครบ แต่ควรตรวจข้อกำหนดของแต่ละคำขอก่อนยื่น " + TAIL },
      { q: "NZQA ต้องใช้คำแปลแบบไหน?", a: "ต้องเป็นคำแปลจากผู้แปลที่มีคุณสมบัติพร้อมสำเนาเอกสารต้นฉบับ ควรตรวจเช็กลิสต์ของ NZQA ตามประเภทวุฒิ" },
      { q: "ต้องรับรองกงสุลเพิ่มไหม?", a: "โดยทั่วไปไม่ต้องสำหรับการยื่นตรวจคนเข้าเมือง เว้นแต่หน่วยงานปลายทางระบุไว้เป็นการเฉพาะ" },
    ],
    synonyms: ["inz translation requirements", "แปลยื่นนิวซีแลนด์", "nzqa translation naati"],
  },
  {
    slug: "naati-certified-vs-recognised",
    title: "NAATI Certified Translator กับ Recognised Practising Translator ต่างกันอย่างไร",
    description:
      "เทียบระดับการรับรองของ NAATI ให้เข้าใจง่าย ระดับไหนใช้ยื่นเอกสารได้ และควรตรวจอะไรก่อนจ้างนักแปล",
    h1: "NAATI Certified vs Recognised Practising — ต่างกันอย่างไร",
    aLabel: "Certified Translator",
    bLabel: "Recognised Practising Translator",
    answer:
      "Certified Translator คือผู้ที่สอบผ่านมาตรฐานการรับรองของ NAATI ในคู่ภาษานั้น ส่วน Recognised Practising Translator ใช้กับภาษาที่ NAATI ยังไม่เปิดสอบรับรอง โดยยืนยันจากประสบการณ์ทำงานล่าสุด สำหรับคู่ภาษาไทย-อังกฤษมีการสอบรับรองอยู่แล้ว งานยื่นเอกสารจึงควรใช้ Certified Translator",
    rows: [
      { aspect: "ที่มา", a: "สอบผ่านมาตรฐาน NAATI", b: "ยืนยันด้วยประสบการณ์ในภาษาที่ยังไม่มีการสอบ" },
      { aspect: "ใช้กับไทย-อังกฤษ", a: "ใช่ เป็นระดับที่ควรเลือก", b: "โดยทั่วไปไม่เกี่ยวข้อง" },
      { aspect: "ต่ออายุ", a: "ทุก 3 ปี", b: "ทุก 3 ปี" },
      { aspect: "ตรวจสอบ", a: "ค้นชื่อ/Practitioner ID ในไดเรกทอรีทางการ", b: "ค้นได้ในไดเรกทอรีเช่นกัน" },
    ],
    chooseA: ["งานแปลไทย-อังกฤษเพื่อยื่นวีซ่า การศึกษา หรือหน่วยงานวิชาชีพ"],
    chooseB: ["ภาษาที่ NAATI ยังไม่เปิดสอบรับรอง"],
    faqs: [
      { q: "ต้องขออะไรจากนักแปลก่อนจ้าง?", a: "ขอหมายเลข Practitioner คู่ภาษา ทิศทางการแปล (ไทย→อังกฤษ) และวันหมดอายุ แล้วตรวจในไดเรกทอรีทางการก่อนเริ่มงาน " + TAIL },
      { q: "ระบบ Accreditation แบบเก่ายังใช้ได้ไหม?", a: "ระบบเดิมถูกแทนที่ด้วย Certification Scheme แล้ว ควรใช้นักแปลที่สถานะการรับรองยังไม่หมดอายุ" },
      { q: "ตราประทับต้องมีข้อมูลอะไรบ้าง?", a: "ควรมีชื่อผู้แปล หมายเลข Practitioner คู่ภาษา ทิศทางการแปล และวันหมดอายุการรับรอง" },
    ],
    synonyms: ["naati certified vs recognised", "ระดับ naati", "naati certification levels"],
  },
];

export const NAATI_COMPARE_BY_SLUG: Record<string, NaatiCompare> = Object.fromEntries(
  NAATI_COMPARE.map((c) => [c.slug, c]),
);
